import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ChevronDown, ChevronRight, LayoutGrid, Lock } from 'lucide-react';
import { apiFetch, ApiClientError } from '@/lib/api';
import { toast } from '@/stores/toast';
import { useAuthStore, hasPermission, hasTenantWidePermission } from '@/stores/auth';
import { useLabel } from '@/lib/labels';
import { formatDateLabel } from '@/lib/format';
import { Button } from '@/components/Button';
import { EmptyState } from '@/components/EmptyState';
import { Modal } from '@/components/Modal';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import type { NodesResponse } from '@/stores/scope';
import { DRAFT_STATUS_LABELS } from '@/lib/shift-format';
import { flattenNodes } from '@/lib/node-tree';

/**
 * `/shift/periods` (v0.4 章④ F-1、D-48②/D-62/D-66)。
 * シフト期間の一覧・全店進捗の可視化 + 新規期間の生成 + 全社最終確定。
 * 設計: docs/design/v04-shift-labor-management-detail.md §4-10/§4-11
 *
 * 編成ボード (`/shift/periods/:id/board`、店舗案の作成そのもの) は F-2 で実装するため、
 * このページは「期間を作る」「進捗を見る」「(揃わなくても) 確定する」までを担う。
 */

type PeriodListItem = {
  period_id: string;
  scope_node_id: string;
  start_date: string;
  end_date: string;
  label: string;
  status: 'open' | 'coordinating' | 'finalized' | 'closed';
  finalized_at: string | null;
  closed_at: string | null;
  notified_at: string | null;
  created_at: string;
  node_progress: Record<string, number>;
  visible_node_count: number;
};

type PeriodNodeState = {
  node_id: string;
  node_name: string;
  draft_status: string;
  submitted_at: string | null;
  deadline_at: string | null;
  deadline_override_at: string | null;
  unsubmitted_handling: string | null;
};

const STATUS_LABELS: Record<string, string> = {
  open: '受付中', coordinating: '調整中', finalized: '確定済み', closed: '締め済み',
};
const UNSUBMITTED_HANDLING_LABELS: Record<string, string> = {
  own_only: '自店のみで確定', followup_round: '後追いラウンドで調整', hq_proxy: '本部が代行',
};
const DONE_DRAFT_STATUSES = new Set(['submitted', 'coordinated', 'confirmed']);

function StatusBadge({ status }: { status: string }) {
  const done = status === 'finalized' || status === 'closed';
  // design-expert MEDIUM-1 指摘: finalized/closed が同色バッジのため、質的に異なる終端状態
  // (closed は編集不可) が一覧の流し読みで見分けづらい。closed のみロックアイコンで差別化する。
  return (
    <span className={`shrink-0 inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border ${
      done ? 'bg-misespark-navy/10 text-misespark-navy border-misespark-navy/20' : 'bg-surface-sunken text-text-sub border-surface-border'
    }`}
    >
      {status === 'closed' && <Lock size={11} aria-hidden />}
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function DraftStatusBadge({ status }: { status: string }) {
  const done = DONE_DRAFT_STATUSES.has(status);
  return (
    <span className={`shrink-0 text-xs px-2 py-1 rounded-full border ${
      done ? 'bg-misespark-navy/10 text-misespark-navy border-misespark-navy/20' : 'bg-surface-sunken text-text-sub border-surface-border'
    }`}
    >
      {DRAFT_STATUS_LABELS[status] ?? status}
    </span>
  );
}

function NodeRow({ periodId, node, canOpenBoard }: { periodId: string; node: PeriodNodeState; canOpenBoard: boolean }) {
  const deadline = node.deadline_override_at ?? node.deadline_at;
  const isOverdue = !!deadline && deadline < new Date().toISOString() && !DONE_DRAFT_STATUSES.has(node.draft_status);
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5">
      <div className="min-w-0">
        <div className="text-sm text-text-body truncate">{node.node_name}</div>
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
          {deadline && (
            <span className={`text-xs ${isOverdue ? 'text-alert-text font-semibold' : 'text-text-sub'}`}>
              締切: {formatDateLabel(deadline)}{isOverdue ? ' (超過)' : ''}
            </span>
          )}
          {node.unsubmitted_handling && (
            <span className="text-[11px] text-text-sub">
              未提出時: {UNSUBMITTED_HANDLING_LABELS[node.unsubmitted_handling] ?? node.unsubmitted_handling}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <DraftStatusBadge status={node.draft_status} />
        {canOpenBoard && (
          <Link
            to={`/shift/periods/${periodId}/board?node_id=${node.node_id}`}
            className="inline-flex items-center gap-1 min-h-[44px] px-2 text-xs text-misespark-navy hover:underline"
          >
            <LayoutGrid size={13} aria-hidden />編成ボード
          </Link>
        )}
      </div>
    </div>
  );
}

function PeriodCard({
  period, expanded, onToggle, canFinalize, onFinalize, canOpenBoard, canClose, onClose, canReopen, onReopen,
}: {
  period: PeriodListItem;
  expanded: boolean;
  onToggle: () => void;
  canFinalize: boolean;
  onFinalize: () => void;
  canOpenBoard: boolean;
  canClose: boolean;
  onClose: () => void;
  canReopen: boolean;
  onReopen: () => void;
}) {
  const { data, isLoading } = useQuery<{ period: PeriodListItem; nodes: PeriodNodeState[] }>({
    queryKey: ['shift-period-nodes', period.period_id],
    queryFn: () => apiFetch(`/shift/periods/${period.period_id}/nodes`),
    enabled: expanded,
  });
  const progressEntries = Object.entries(period.node_progress);

  return (
    <div className="border border-surface-border rounded-lg bg-white overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="w-full flex flex-wrap items-center justify-between gap-2 px-4 py-3 min-h-[44px] text-left hover:bg-surface-sunken"
      >
        <div className="flex items-center gap-2 min-w-0">
          {expanded
            ? <ChevronDown size={16} className="shrink-0 text-text-sub" aria-hidden />
            : <ChevronRight size={16} className="shrink-0 text-text-sub" aria-hidden />}
          <div className="min-w-0">
            <div className="text-sm font-semibold text-text-body truncate">{period.label}</div>
            <div className="text-xs text-text-sub">
              {formatDateLabel(period.start_date)} 〜 {formatDateLabel(period.end_date)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={period.status} />
          <span className="text-xs text-text-sub">{period.visible_node_count}店舗</span>
        </div>
      </button>

      {progressEntries.length > 0 && (
        <div className="px-4 pb-3 flex flex-wrap gap-1.5">
          {progressEntries.map(([status, count]) => (
            <span key={status} className="text-xs px-2 py-1 rounded-full bg-surface-sunken text-text-sub">
              {DRAFT_STATUS_LABELS[status] ?? status} {count}
            </span>
          ))}
        </div>
      )}

      {(canFinalize || canClose || canReopen) && (
        <div className="px-4 pb-3 flex flex-wrap gap-2">
          {canFinalize && <Button variant="secondary" size="sm" onClick={onFinalize}>この期間を確定する</Button>}
          {canClose && <Button variant="secondary" size="sm" onClick={onClose}>この期間を締める</Button>}
          {canReopen && <Button variant="ghost" size="sm" onClick={onReopen}>締めを解除する</Button>}
        </div>
      )}

      {expanded && (
        <div className="border-t border-surface-border">
          {isLoading ? (
            <div className="p-4 text-sm text-text-sub">読み込み中…</div>
          ) : (data?.nodes.length ?? 0) === 0 ? (
            <div className="p-4 text-sm text-text-sub">対象店舗がありません。</div>
          ) : (
            <div className="divide-y divide-surface-border">
              {data!.nodes.map((n) => <NodeRow key={n.node_id} periodId={period.period_id} node={n} canOpenBoard={canOpenBoard} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CreatePeriodModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { data: nodesData } = useQuery<NodesResponse>({ queryKey: ['nodes'], queryFn: () => apiFetch<NodesResponse>('/nodes') });
  const flatNodes = useMemo(() => flattenNodes(nodesData?.roots ?? []), [nodesData]);
  const [nodeId, setNodeId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [label, setLabel] = useState('');

  const createMut = useMutation({
    mutationFn: () => apiFetch<{ period_id: string; store_count: number }>('/shift/periods', {
      method: 'POST',
      body: JSON.stringify({ scope_node_id: nodeId, start_date: startDate, end_date: endDate, label: label.trim() }),
    }),
    onSuccess: (res) => { toast.success(`期間を作成しました (対象 ${res.store_count} 店舗)`); onCreated(); },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '作成に失敗しました'),
  });

  const canSubmit = !!nodeId && !!startDate && !!endDate && startDate <= endDate && label.trim().length > 0;

  return (
    <Modal
      open
      title="新しいシフト期間を作成"
      onClose={onClose}
      size="sm"
      footer={(
        <>
          <Button variant="secondary" onClick={onClose}>キャンセル</Button>
          <Button variant="primary" disabled={!canSubmit} loading={createMut.isPending} onClick={() => createMut.mutate()}>
            作成する
          </Button>
        </>
      )}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-sm text-text-sub mb-1">対象範囲</label>
          <Select value={nodeId || undefined} onValueChange={setNodeId}>
            <SelectTrigger className="w-full min-h-[44px]"><SelectValue placeholder="ノードを選ぶ" /></SelectTrigger>
            <SelectContent>
              {flatNodes.map(({ node, depth }) => (
                <SelectItem key={node.node_id} value={node.node_id}>{'　'.repeat(depth)}{node.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm text-text-sub mb-1">開始日</label>
            <input
              type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
            />
          </div>
          <div>
            <label className="block text-sm text-text-sub mb-1">終了日</label>
            <input
              type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
              className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
            />
          </div>
        </div>
        <div>
          <label className="block text-sm text-text-sub mb-1">名称</label>
          <input
            value={label} onChange={(e) => setLabel(e.target.value)} maxLength={200}
            placeholder="例: 2026年9月前半"
            className="w-full min-h-[44px] px-3 py-2 rounded border border-surface-border focus-ring"
          />
        </div>
      </div>
    </Modal>
  );
}

export function ShiftPeriodsPage() {
  const t = useLabel();
  const user = useAuthStore((s) => s.user);
  const qc = useQueryClient();
  // ⚠️ TD-22: シフト期間の作成・確定・締め・再開はいずれも requireTenantWideManage
  // (routes/shift-periods.ts) で全店舗スコープを要求する。UI 側でも同じ条件を再現する。
  const canManage = hasTenantWidePermission(user?.effective_permissions, 'shift_periods', 'manage');
  // v0.4 章④ F-2: 編成ボードへのリンク表示可否。バックエンド (GET .../board) は shift:view のみを
  // 要求するため (QA指摘 L-4)、閲覧専用ロールにも導線を出す。編集操作自体はボード側の
  // canUpdate (shift:update) でボタン単位に制御される。実際のノード単位scope判定はボード側APIが担保する。
  const canOpenBoard = hasPermission(user?.effective_permissions, 'shift_periods', 'view');
  const [createOpen, setCreateOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [finalizeTarget, setFinalizeTarget] = useState<PeriodListItem | null>(null);
  const [closeTarget, setCloseTarget] = useState<PeriodListItem | null>(null);
  const [reopenTarget, setReopenTarget] = useState<PeriodListItem | null>(null);

  const { data, isLoading, isError } = useQuery<{ periods: PeriodListItem[] }>({
    queryKey: ['shift-periods'],
    queryFn: () => apiFetch('/shift/periods'),
  });
  const periods = data?.periods ?? [];

  const finalizeMut = useMutation({
    mutationFn: (periodId: string) => apiFetch<{ unsubmitted_nodes: { node_id: string; draft_status: string }[] }>(
      `/shift/periods/${periodId}/finalize`, { method: 'POST' },
    ),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['shift-periods'] });
      setFinalizeTarget(null);
      toast.success(res.unsubmitted_nodes.length > 0
        ? `確定しました (未提出の店舗が${res.unsubmitted_nodes.length}件あります)`
        : '確定しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '確定に失敗しました'),
  });

  const closeMut = useMutation({
    mutationFn: (periodId: string) => apiFetch<{ status: string }>(`/shift/periods/${periodId}/close`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-periods'] });
      setCloseTarget(null);
      toast.success('期間を締めました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '締めに失敗しました'),
  });

  const reopenMut = useMutation({
    mutationFn: (periodId: string) => apiFetch<{ status: string }>(`/shift/periods/${periodId}/reopen`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['shift-periods'] });
      setReopenTarget(null);
      toast.success('締めを解除しました');
    },
    onError: (e) => toast.error(e instanceof ApiClientError ? e.message : '締めの解除に失敗しました'),
  });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-h2">{t('entity.shift_periods', 'シフト期間')}</h2>
          <p className="text-sm text-text-sub mt-1">店舗ごとのシフト案作成の進捗を確認できます。</p>
        </div>
        {canManage && (
          <Button variant="primary" leftIcon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>
            新しい期間を作成
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="text-text-sub">読み込み中…</div>
      ) : isError ? (
        <div className="p-6 border border-surface-border rounded-lg bg-white text-text-sub">期間の取得に失敗しました。</div>
      ) : periods.length === 0 ? (
        <EmptyState
          message="シフト期間がまだありません"
          description="新しい期間を作成すると、各店舗が希望収集・シフト案作成を始められます。"
          actionLabel={canManage ? '新しい期間を作成' : undefined}
          onAction={canManage ? () => setCreateOpen(true) : undefined}
        />
      ) : (
        <div className="space-y-3">
          {periods.map((p) => (
            <PeriodCard
              key={p.period_id}
              period={p}
              expanded={expandedId === p.period_id}
              onToggle={() => setExpandedId((cur) => (cur === p.period_id ? null : p.period_id))}
              canFinalize={canManage && (p.status === 'open' || p.status === 'coordinating')}
              onFinalize={() => setFinalizeTarget(p)}
              canOpenBoard={canOpenBoard}
              canClose={canManage && p.status === 'finalized'}
              onClose={() => setCloseTarget(p)}
              canReopen={canManage && p.status === 'closed'}
              onReopen={() => setReopenTarget(p)}
            />
          ))}
        </div>
      )}

      {createOpen && (
        <CreatePeriodModal
          onClose={() => setCreateOpen(false)}
          onCreated={() => { setCreateOpen(false); qc.invalidateQueries({ queryKey: ['shift-periods'] }); }}
        />
      )}

      <Modal
        open={!!finalizeTarget}
        title="シフト期間を確定しますか？"
        onClose={() => setFinalizeTarget(null)}
        size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setFinalizeTarget(null)}>キャンセル</Button>
            <Button
              variant="primary" loading={finalizeMut.isPending}
              onClick={() => finalizeTarget && finalizeMut.mutate(finalizeTarget.period_id)}
            >
              確定する
            </Button>
          </>
        )}
      >
        <p className="text-sm text-text-body">
          「{finalizeTarget?.label}」を全社確定します。確定すると、割当のある従業員へ通知が送られます。
          未提出の店舗がある場合も確定できます (後から調整ラウンドで対応できます) が、確定後は既に確定した他店の応援枠を動かせなくなります。
        </p>
      </Modal>

      <Modal
        open={!!closeTarget}
        title="この期間を締めますか？"
        onClose={() => setCloseTarget(null)}
        size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setCloseTarget(null)}>キャンセル</Button>
            <Button
              variant="primary" loading={closeMut.isPending}
              onClick={() => closeTarget && closeMut.mutate(closeTarget.period_id)}
            >
              締める
            </Button>
          </>
        )}
      >
        <p className="text-sm text-text-body">
          「{closeTarget?.label}」を締めます。締めた期間は割当の作成・変更・取消ができなくなり、下書き自動生成の対象からも外れます。
          給与・人件費の確定後に締めることを推奨します。締めた後も再開できます。
        </p>
      </Modal>

      <Modal
        open={!!reopenTarget}
        title="この期間の締めを解除しますか？"
        onClose={() => setReopenTarget(null)}
        size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setReopenTarget(null)}>キャンセル</Button>
            <Button
              variant="primary" loading={reopenMut.isPending}
              onClick={() => reopenTarget && reopenMut.mutate(reopenTarget.period_id)}
            >
              解除する
            </Button>
          </>
        )}
      >
        <p className="text-sm text-text-body">
          「{reopenTarget?.label}」の締めを解除し、確定済みの状態に戻します。解除すると再び割当の編集ができるようになります。
        </p>
      </Modal>
    </div>
  );
}
